<template>
  <chart-container :titleChart="titleChart" @zoomEnabled="zoomEnabled" @zoomDisabled="zoomDisabled" :zoomGraphValue="zoomGraphValue" :downloadId="downloadId" :downloadShow="false" :isZoomEnabled="dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0">
    <div class="float-right d-flex align-items-center" slot="header" v-if="dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0">
      <div class="header-filter-info">
        Analyte Filter
        <div class="info-custom-tooltip">
          <i class="fa fa-info-circle" />
          <div class="info-custom-tooltiptext">
            <p class="mb-6"><strong>FVC -</strong> Actual <strong>Forced Vital Capacity</strong> representing the full amount of air that can be exhaled with effort in a complete breath.</p>
            <p class="mb-6"><strong>FEV1 -</strong> Actual <strong>Forced Expiratory Volume</strong> representing the amount of exhaled air during the first second of the spirometry test.</p>
            <p class="mb-6"><strong>FEV1 FVC -</strong> Actual <strong>Forced Expiratory Volume</strong> over <strong>Forced Vital Capacity</strong> representing the ratio for the amount of air you can forcefully exhale.</p>
            <p class="mb-6"><strong>FEV6 -</strong> Actual <strong>Forced Expiratory Volume</strong> representing the amount of air exhaled during the six-second spirometry test.</p>
            <p class="mb-6"><strong>FEF2575 - </strong><strong>Forced Expiratory Flow</strong> measured at 25% and 75% of the pulmonary volume representing the mean forced expiratory flow rate.</p>
            <p class="mb-0"><strong>PEF -</strong> Actual <strong>Peak Expiratory Flow</strong> representing the maximum speed of expiration.</p>
          </div>
        </div>
      </div>

      <div class="analyte-filter d-inline-block">
        <b-dropdown id="analyte-filter" variant="outline-secondary" class="dropdown-analyte-filter" ref="analyteFilter">
          <template slot="button-content">
            <span v-if="selectedAnalyteFilter" class="label-selected">
              {{ selectedAnalyteFilter == "FEV1/FVC" ? "FEV1 FVC" : selectedAnalyteFilter }}
            </span>
          </template>

          <div class="dropdown-menu-wrapper" v-if="sortedAnalyteFilters && sortedAnalyteFilters.length > 0">
            <div class="dropdown-item-flex-wrapper">
              <div v-for="(row, index) in sortedAnalyteFilters" :key="index" class="item-menu-participant-dropdown" :class="row == selectedAnalyteFilter ? 'active' : ''" @click="analyteFilterSelected(row)">
                <div class="dropdownitemmenu">
                  {{ row == "FEV1/FVC" ? "FEV1 FVC" : row }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="no-data-participants">
            <span>No data in</span>
          </div>
        </b-dropdown>
      </div>
    </div>

    <fusioncharts style="width: 100%" :type="type" :width="width" :height="height" :dataFormat="dataFormat" :dataSource="dataSource" dataEmptyMessage="No Data In" ref="dailySpirometerReadingChart" v-if="dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0" :events="fcEvents" slot="body" />

    <div slot="body" class="text-left min-height-0" v-if="dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0">
      <template v-if="zoomTrigger">
        <span @click="zoomOutChart()" class="pl-10 pr-5 remove-zoom-icons">
          <img src="/static/img/icon-zoomout.svg" class="download_graph cursor_pointer" aria-haspopup="true" aria-expanded="false" title="Zoom Out" />
        </span>
        <span @click="zoomInChart()" class="remove-zoom-icons">
          <img src="/static/img/icon-zoomin.svg" class="download_graph cursor_pointer" aria-haspopup="true" aria-expanded="false" title="Zoom In" />
        </span>
      </template>
    </div>

    <div v-else slot="body" class="align-center p-60">
      <div class="card-text-title no-data" v-if="dataLoaded">No Data In</div>
    </div>
  </chart-container>
</template>

<script>
import ChartContainer from "./chart-container.vue";
import _cloneDeep from "lodash/cloneDeep";
import * as forcedSpirometryService from "@/services/visualization/forced-spirometry";

export default {
  components: {
    ChartContainer
  },

  props: {
    studyID: { type: String, default: "" },
    titleChart: [String],
    zoomGraphValue: { type: String, default: "" },
    downloadId: { type: String, default: "" },
    rawData: { type: [Object, Array], default: () => {} },
    dataLoaded: { type: Boolean, default: false },
    sitesSelected: [Array, String],
    countrySelected: [Array, String],
    analyteFilters: { type: Array, default: () => [] }
  },

  watch: {
    rawData: function(newVal) {
      const response = forcedSpirometryService.generateDailySpirometerReadingData(this.selectedAnalyteFilter, newVal, this.countrySelected, this.sitesSelected);

      const dataSources = _cloneDeep(this.dataSource);
      if (response.category && response.category.length > 0 && response.data && response.data.length > 0) {
        dataSources.categories[0] = { category: response.category };
        dataSources.dataset[0] = { data: response.data };
      } else {
        dataSources.categories[0] = { category: [] };
        dataSources.dataset[0] = { data: [] };
      }
      dataSources.chart.paletteColors = forcedSpirometryService.primaryColorFnc() + ",#a6cee3,#7891d1,#56b3b9,#cab2d6,#7f69d4,#fb9a99,#fdbf6f,#b5cdd8";
      dataSources.chart.toolTipBgColor = forcedSpirometryService.primaryColorFnc();
      dataSources.chart.toolTipBorderColor = forcedSpirometryService.primaryColorFnc();
      dataSources.chart.toolTipColor = forcedSpirometryService.getContrast(forcedSpirometryService.primaryColorFnc());
      this.dataSource = dataSources;
    },
    countrySelected: function(newVal) {
      const response = forcedSpirometryService.generateDailySpirometerReadingData(this.selectedAnalyteFilter, this.rawData, newVal, this.sitesSelected);
      if (response.data && response.data.length > 0) {
        this.changeChartPropsOnZoomTrigger(response);
      }
    },
    sitesSelected: function(newVal) {
      const response = forcedSpirometryService.generateDailySpirometerReadingData(this.selectedAnalyteFilter, this.rawData, this.countrySelected, newVal);
      if (response.data && response.data.length > 0) {
        this.changeChartPropsOnZoomTrigger(response);
      }
    },
    zoomTrigger: function() {
      this.height = this.zoomTrigger ? "400" : "250";
      this.changeChartPropsOnZoomTrigger();
    },
    zoomGraphValue: function(newVal) {
      this.zoomGraphValueChild = newVal;
      if (newVal == "") {
        this.zoomDisabled();
      }
    },
    selectedAnalyteFilter: function(newValOld) {
      let newVal = newValOld;
      if (newVal == "FEV1 FVC") {
        newVal = "FEV1/FVC";
      }
      const response = forcedSpirometryService.generateDailySpirometerReadingData(newVal, this.rawData, this.countrySelected, this.sitesSelected);
      if (response.data && response.data.length > 0) {
        this.changeChartPropsOnZoomTrigger(response);
      }
    }
  },

  data() {
    return {
      zoomTrigger: false,
      selectedAnalyteFilter: "FVC",
      height: "250",
      width: "100%",
      type: "scrollline2d",
      dataFormat: "json",
      dataSource: {
        chart: {
          xAxisName: "Mean Results",
          xAxisNameFontColor: "#959fa4",
          xAxisNameFont: "RobotoMedium",
          xAxisNameFontSize: "9",
          xAxisNamePadding: "15",
          yAxisNamePadding: "9",
          yAxisName: "Spirometer Readings",
          yAxisNameFontColor: "#959fa4",
          yAxisNameFont: "RobotoMedium",
          yAxisNameFontSize: "9",
          yAxisValueFontColor: "a3a3a3",
          yAxisValueFont: "RobotoRegular",
          yAxisValueFontSize: "10",
          outCnvBaseFontSize: "10",
          outCnvBaseFont: "RobotoRegular",
          outCnvBaseFontColor: "#b0bac9",
          lineThickness: "3",
          flatScrollBars: "1",
          scrollheight: "8",
          numVisiblePlot: "5",
          showHoverEffect: "1",
          anchorRadius: "10",
          anchorImageAlpha: "100",
          anchorImageScale: "40",
          anchorImagePadding: "0",
          chartTopPadding: "10",
          showValues: "0",
          yAxisValuesStep: "2",
          theme: "fusion",
          chartBottomMargin: "0",
          chartTopMargin: "20",
          anchorHoverEffect: "0",
          useEllipsesWhenOverflow: "1",
          scrollToEnd: "1",
          labelPadding: "13",
          chartLeftMargin: "3",
          showxaxisline: "1",
          showyaxisline: "1",
          yAxisLineColor: "#b0bac9",
          xAxisLineColor: "#b0bac9",
          yaxislinethickness: "1",
          xaxislinethickness: "1",
          seriesNameInToolTip: "0",
          plotColorinTooltip: "0",
          tooltipPosition: "top",
          divLineAlpha: "0",
          canvasLeftPadding: 40,
          canvasRightPadding: 40,
          canvasTopPadding: 15,
          canvasBottomPadding: 15,
          scrollPadding: 1
        },
        categories: [],
        dataset: []
      },
      fcEvents: {
        dataUpdated: () => {
          setTimeout(() => {
            this.setLabelWidth();
          }, 1000);
        },
        onScroll: () => {
          setTimeout(() => {
            this.setLabelWidth();
          }, 1000);
        }
      },
      curZoomLevel: 0,
      maxZoomLevel: 3,
      sortOrder: ["FVC", "FEV1", "FEV1 FVC", "FEV6", "FEF2575", "PEF"],
      sortedAnalyteFilters: []
    };
  },

  methods: {
    zoomEnabled() {
      this.$emit("zoomEnabled");
      this.zoomTrigger = true;
    },
    zoomDisabled() {
      this.$emit("zoomDisabled");
      this.zoomTrigger = false;
    },
    analyteFilterSelected(row) {
      this.selectedAnalyteFilter = row;
      this.$refs.analyteFilter.hide();
    },
    changeChartPropsOnZoomTrigger(response = null, numVisiblePlot = this.getNumVisPlotByZoom()) {
      if (this.$refs.dailySpirometerReadingChart) {
        const chartRef = this.$refs.dailySpirometerReadingChart.chartObj;
        const data = chartRef.getJSONData();
        if (this.zoomTrigger) {
          data.chart.xAxisNameFontSize = "14";
          data.chart.yAxisNameFontSize = "14";
          data.chart.numVisiblePlot = numVisiblePlot;
          this.dataSource.chart.xAxisNameFontSize = "14";
          this.dataSource.chart.yAxisNameFontSize = "14";
          this.dataSource.chart.numVisiblePlot = numVisiblePlot;
        } else {
          data.chart.xAxisNameFontSize = "9";
          data.chart.yAxisNameFontSize = "9";
          data.chart.numVisiblePlot = "5";
          this.dataSource.chart.xAxisNameFontSize = "9";
          this.dataSource.chart.yAxisNameFontSize = "9";
          this.dataSource.chart.numVisiblePlot = "5";
        }
        if (response) {
          data.categories[0] = { category: response.category };
          data.dataset[0] = { data: response.data };
        }
        data.chart.paletteColors = forcedSpirometryService.primaryColorFnc() + ",#a6cee3,#7891d1,#56b3b9,#cab2d6,#7f69d4,#fb9a99,#fdbf6f,#b5cdd8";
        data.chart.toolTipBgColor = forcedSpirometryService.primaryColorFnc();
        data.chart.toolTipBorderColor = forcedSpirometryService.primaryColorFnc();
        data.chart.toolTipColor = forcedSpirometryService.getContrast(forcedSpirometryService.primaryColorFnc());
        chartRef.setJSONData(data);
      }
    },

    setLabelWidth() {
      let labelLength = this.dataSource.categories[0].category.length;
      let width;

      if (this.zoomTrigger) {
        if (labelLength > 13) {
          labelLength = 13;
        }
        width = 100 / labelLength;
        if (labelLength < 12) {
          width += 3.4;
        }
      } else {
        if (labelLength > 6) {
          labelLength = 6;
        }
        width = 100 / labelLength;
      }

      if (labelLength == 1) {
        width = "80px";
      } else {
        width += "%";
      }

      if (labelLength > 0 && document.querySelectorAll('#daily-spirometer-reading .fusioncharts-container [class$="-dataset-Label-group"]').length > 0) {
        const selector = '#daily-spirometer-reading .fusioncharts-container [class$="-dataset-Label-group"]';
        const $elements = document.querySelectorAll(selector);
        $elements[$elements.length - 3].querySelectorAll("rect").forEach(el => {
          el.style.width = width;
        });
      }
    },
    getNumVisPlotByZoom() {
      let numVisPlot = 0;
      switch (this.curZoomLevel) {
        case 0:
          numVisPlot = 12;
          break;
        case 1:
          numVisPlot = 10;
          break;
        case 2:
          numVisPlot = 8;
          break;
        case 3:
          numVisPlot = 6;
          break;
        default:
          break;
      }
      return numVisPlot;
    },
    zoomInChart() {
      if (this.curZoomLevel < this.maxZoomLevel) {
        this.curZoomLevel = this.curZoomLevel + 1;
        const numVisPlot = this.getNumVisPlotByZoom();
        this.changeChartPropsOnZoomTrigger(null, numVisPlot);
      }
    },
    zoomOutChart() {
      if (this.curZoomLevel > 0) {
        this.curZoomLevel = this.curZoomLevel - 1;
        const numVisPlot = this.getNumVisPlotByZoom();
        this.changeChartPropsOnZoomTrigger(null, numVisPlot);
      }
    },
    setSortedAnalyteFilters(newVal) {
      const updateIndex = newVal.findIndex(filter => filter == "FEV1/FVC");
      if (updateIndex > -1) {
        newVal[updateIndex] = "FEV1 FVC";
      }
      newVal.sort((a, b) => {
        return this.sortOrder.indexOf(a) - this.sortOrder.indexOf(b);
      });

      this.sortedAnalyteFilters = newVal;
    }
  },

  created() {
    this.unwatchAnalyteFilterProp = this.$watch("analyteFilters", function(newVal) {
      if (newVal) {
        this.setSortedAnalyteFilters(newVal);
        this.unwatchAnalyteFilterProp();
      }
    });
  }
};
</script>

<style lang="scss" scoped>
.analyte-filter {
  padding-right: 5px;
}

.item-menu-participant-dropdown {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 9px !important;

  &:hover,
  &.active {
    background-color: #e4e8eb;
  }

  .dropdownitemmenu {
    display: flex;
    font-family: "Roboto-Medium";
    font-size: 9px !important;
    color: #666669;
    flex-basis: 50%;
    pointer-events: none;
  }
}

.no-data-participants {
  text-align: center;
  font-family: "Roboto-Medium";
  font-size: 12px;
  color: #a3a3a3;
  padding: 18px 0;
  pointer-events: none;
}

.header-filter-info {
  font-size: 9px !important;
  margin-right: 10px;
  color: #b0bac9;
  vertical-align: text-bottom;
}

.info-custom-tooltip {
  position: relative;
  display: inline-block;

  .fa.fa-info-circle {
    font-size: 9px !important;
  }

  .info-custom-tooltiptext {
    font-size: 10px !important;
    font-family: "RobotoRegular";
    visibility: hidden;
    width: 316px;
    white-space: break-spaces;
    background-color: #eff3f5;
    color: #666669;
    text-align: left;
    border-radius: 10px;
    padding: 10px 12px 11px 12px;
    font-weight: 500;
    position: absolute;
    z-index: 1;
    top: 132%;
    right: -156px;
    margin-left: 8px;
    box-shadow: -1px 4px 12px -2px rgba(0, 0, 0, 0.44);
    text-overflow: unset !important;
    white-space: normal !important;
    border: solid 1px #b0bac9;

    &::before {
      content: " ";
      position: absolute;
      bottom: 100%;
      left: 45.53%;
      height: 10px;
      margin-top: -4px;
      border-width: 11.4px;
      border-style: solid;
      border-color: transparent transparent #b0bac9 transparent;
    }

    &::after {
      content: " ";
      position: absolute;
      bottom: 100%;
      left: 46%;
      height: 10px;
      margin-top: -5px;
      border-width: 10px;
      border-style: solid;
      border-color: transparent transparent #eff3f5 transparent;
    }
  }

  &:hover .info-custom-tooltiptext {
    visibility: visible;
  }
}

.daily-spirometer-reading-zoom {
  .header-filter-info {
    font-size: 14px !important;
  }

  .info-custom-tooltip .fa.fa-info-circle {
    font-size: 12px !important;
  }

  .card-header {
    .float-right {
      font-size: 16px;
    }
  }

  #analyte-filter {
    .label-selected {
      font-size: 14px !important;
    }
  }
}
</style>
